import { SagaIterator } from 'redux-saga';
import { all, call, put, select } from 'redux-saga/effects';
import { FeatureFlagName } from '../../../constants/features';
import { PerformanceMetric } from '../../../constants/telemetry';
import {
    ClientError,
    DataResponse,
    FailureOperation,
    isAggregatedFailure,
    isAggregatedSuccess,
} from '../../../models/common';
import { combineResults, getErrorCodes } from '../../../utilities/aggregated-result';
import { aggregateFailureResponses } from '../../../utilities/failure';
import { isFeatureFlagEnabled } from '../../../utilities/features';
import { trackTimedPerformanceMetric } from '../../../utilities/telemetry/channel';
import {
    listDevBoxAddOns,
    loadDevBoxAddOnsForDevBoxes,
    loadDevBoxAddOnsForDevBoxesError,
    loadDevBoxAddOnsForDevBoxesFailed,
    loadDevBoxAddOnsForDevBoxesSuccess,
} from '../../actions/add-ons/add-ons-action-creators';
import { LoadDevBoxAddOnsForDevBoxesAction } from '../../actions/add-ons/add-ons-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { rejectAction } from '../../effects/reject-action';
import { resolveAction } from '../../effects/resolve-action';
import { takeLeading } from '../../effects/take';
import { getDevBoxIds } from '../../selector/dev-box-selectors';
import { AsyncOutcome } from '../../store/common-state';
import { getIdsForDevBoxesRequiringDevBoxAddOns } from './selectors';

export function* loadDevBoxAddOnsForDevBoxesSaga(action: LoadDevBoxAddOnsForDevBoxesAction): SagaIterator {
    const startTime = new Date();

    try {
        const isConnectWithVSCodeEnabled: boolean = yield call(
            isFeatureFlagEnabled,
            FeatureFlagName.EnableConnectWithVSCode
        );

        const ids: string[] = isConnectWithVSCodeEnabled
            ? yield select(getDevBoxIds)
            : yield select(getIdsForDevBoxesRequiringDevBoxAddOns);

        const responses: DataResponse[] = yield all(ids.map((id) => putAndAwait(listDevBoxAddOns({ id }))));

        const completeResult = combineResults(responses);

        if (isAggregatedFailure(completeResult)) {
            const { failures } = completeResult;
            const failure = aggregateFailureResponses(FailureOperation.LoadDevBoxAddOnsForDevBoxes, ...failures);
            yield put(loadDevBoxAddOnsForDevBoxesFailed({ failure }));
        } else {
            yield put(loadDevBoxAddOnsForDevBoxesSuccess());
        }

        yield resolveAction(action, completeResult);

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.LoadDevBoxAddOnsForDevBoxes,
            startTime,
            completeResult.outcome,
            isAggregatedSuccess(completeResult) ? undefined : { errorCodes: getErrorCodes(completeResult) }
        );
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.LoadDevBoxAddOnsForDevBoxes);
        yield put(loadDevBoxAddOnsForDevBoxesError({ error }));
        yield rejectAction(action, error);

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.LoadDevBoxAddOnsForDevBoxes,
            startTime,
            AsyncOutcome.Error,
            { errorCodes: [error.code] }
        );
    }
}

export function* loadDevBoxAddOnsForDevBoxesListenerSaga(): SagaIterator {
    yield takeLeading(loadDevBoxAddOnsForDevBoxes, loadDevBoxAddOnsForDevBoxesSaga);
}
